import {
    HeaderGroup,
    Row,
    useReactTable,
    flexRender,
} from "@tanstack/react-table";
import classNames from "classnames";
import styles from "./ViewTable.module.scss";

const PaddingRow = ({
    height,
    colSpan,
}: {
    height: number;
    colSpan: number;
}) => (
    <tr className={styles.paddingRow}>
        <td
            style={{
                height: `${height}px`,
                padding: 0,
                border: "none",
                background: "none",
            }}
            colSpan={colSpan}
        />
    </tr>
);

const TableHeader = ({
    headerGroups,
}: {
    headerGroups: Array<HeaderGroup<any>>;
}) => (
    <thead>
        {headerGroups.map((headerGroup) => (
            <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                    <th key={header.id} className={header.id}>
                        {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                        )}
                    </th>
                ))}
            </tr>
        ))}
    </thead>
);

interface ViewTableSectionProps<TData> {
    sectionType: "left" | "center" | "right";
    table: ReturnType<typeof useReactTable<TData>>;
    paddingTop: number;
    paddingBottom: number;
    virtualRows: any[];
    getRowProps: (row: Row<TData>) => any;
}

export const ViewTableSection = <TData,>({
    sectionType,
    table,
    paddingTop,
    paddingBottom,
    virtualRows,
    getRowProps,
}: ViewTableSectionProps<TData>) => {
    const getHeaderGroups = () => {
        switch (sectionType) {
            case "left":
                return table.getLeftHeaderGroups();
            case "center":
                return table.getCenterHeaderGroups();
            case "right":
                return table.getRightHeaderGroups();
            default:
                return table.getHeaderGroups();
        }
    };

    const getCells = (row: Row<TData>) => {
        switch (sectionType) {
            case "left":
                return row.getLeftVisibleCells();
            case "center":
                return row.getCenterVisibleCells();
            case "right":
                return row.getRightVisibleCells();
            default:
                return row.getAllCells();
        }
    };

    const headerGroups = getHeaderGroups();
    const headerCount = headerGroups[0]?.headers.length ?? 0;

    // Component for style classes based on section type
    const containerClassName = () => {
        switch (sectionType) {
            case "left":
                return styles.pinnedLeft;
            case "center":
                return classNames(styles.centerScrollable, {
                    [styles.hasLeftPinned]:
                        table.getLeftHeaderGroups()[0]?.headers.length > 0,
                    [styles.hasRightPinned]:
                        table.getRightHeaderGroups()[0]?.headers.length > 0,
                });
            case "right":
                return styles.pinnedRight;
            default:
                return "";
        }
    };

    // If this section has no columns, don't render anything
    if (headerCount === 0) {
        return null;
    }

    return (
        <div
            className={containerClassName()}
            style={{
                overflow: sectionType === "center" ? "auto" : "visible",
                overflowY: "visible",
            }}
            ref={
                sectionType === "center"
                    ? (table.options.meta as any)?.centerScrollRef
                    : undefined
            }
        >
            <table className={classNames("table", styles.table)}>
                <TableHeader headerGroups={headerGroups} />
                <tbody>
                    {paddingTop > 0 && (
                        <PaddingRow height={paddingTop} colSpan={headerCount} />
                    )}
                    {virtualRows.map((virtualRow) => {
                        const row = table.getRowModel().rows[virtualRow.index];
                        return (
                            <tr key={row.id} {...getRowProps(row)}>
                                {getCells(row).map((cell) => (
                                    <td
                                        key={cell.id}
                                        className={cell.column.id}
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                    {paddingBottom > 0 && (
                        <PaddingRow
                            height={paddingBottom}
                            colSpan={headerCount}
                        />
                    )}
                </tbody>
            </table>
        </div>
    );
};
